import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, RefreshCw } from "lucide-react";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Field, Select, Btn, MpinField, DataTable, StatusBadge } from "@/components/shared";

const PREVIEW_COLS = [
  { label: "Row", key: "row" }, { label: "Beneficiary", key: "holder_name" },
  { label: "Account No.", render: (r) => <span className="font-mono text-xs">{r.account_no}</span> },
  { label: "IFSC", render: (r) => <span className="font-mono text-xs">{r.ifsc}</span> }, { label: "Bank", key: "bank" }, { label: "Mobile", key: "mobile" }, { label: "Mode", key: "mode" },
  { label: "Amount", render: (r) => <span className="font-mono">{inr(r.amount)}</span> },
  { label: "Charge", render: (r) => <span className="font-mono">{inr(r.charge)}</span> },
  { label: "Check", render: (r) => (r.error ? <span className="text-xs font-semibold text-red-600">{r.error}</span> : <span className="text-xs font-bold text-emerald-700">OK</span>) },
];

const BATCH_ROW_COLS = [
  { label: "#", key: "n" }, { label: "Beneficiary", key: "holder_name" },
  { label: "Account", render: (r) => <span className="font-mono text-xs">{r.account_no} · {r.ifsc}</span> },
  { label: "Amount", render: (r) => <span className="font-mono">{inr(r.amount)}</span> },
  { label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  { label: "UTR", render: (r) => <span className="font-mono text-xs">{r.utr || "-"}</span> },
  { label: "Txn ID", render: (r) => <span className="font-mono text-xs">{r.txn_id || "-"}</span> },
  { label: "Remarks", render: (r) => <span className="text-xs text-slate-500">{r.remarks || "-"}</span> },
];

async function downloadTemplate() {
  try {
    const { data } = await api.get("/payout/bulk/template", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = "move-to-bank-bulk.xlsx";
    a.click();
  } catch (e) { toast.error(errMsg(e)); }
}

function Batches({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/payout/bulk/batches").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  const busy = rows.some((b) => b.counts.PENDING || b.counts.QUEUED);
  useEffect(() => { if (!busy) return undefined; const t = setInterval(load, 15000); return () => clearInterval(t); }, [busy]);
  return (
    <Card className="mt-6" title="Bulk uploads" action={<Btn variant="outline" onClick={load} data-testid="bulk-batches-refresh"><RefreshCw className="h-4 w-4" />Refresh</Btn>}>
      <div className="mb-2 text-xs text-slate-500">Status and UTR update automatically when the bank confirms each transfer.</div>
      <DataTable testid="bulk-batches-table" rows={rows} columns={[
        { label: "Date", render: (b) => <span className="text-slate-500">{fmtDate(b.created_at)}</span> },
        { label: "Batch", render: (b) => <span className="font-mono text-xs">{b.batch_no}</span> },
        { label: "Rows", key: "count" }, { label: "Amount", render: (b) => <span className="font-mono">{inr(b.total_amount)}</span> },
        { label: "Success", render: (b) => <span className="font-semibold text-emerald-700">{b.counts.SUCCESS}</span> },
        { label: "Pending", render: (b) => <span className="font-semibold text-amber-600">{b.counts.PENDING + b.counts.QUEUED}</span> },
        { label: "Failed", render: (b) => <span className="font-semibold text-red-600">{b.counts.FAILED + b.counts.NOT_SENT}</span> },
        { label: "", render: (b) => <button className="text-xs font-semibold text-blue-700" onClick={() => setOpen(open?.id === b.id ? null : b)} data-testid={`bulk-batch-view-${b.batch_no}`}>{open?.id === b.id ? "Hide" : "View"}</button> },
      ]} />
      {open && <div className="mt-4"><DataTable testid="bulk-batch-rows-table" rows={rows.find((b) => b.id === open.id)?.rows || []} columns={BATCH_ROW_COLS} /></div>}
    </Card>
  );
}

export default function BulkPayout() {
  const { wallet, refreshBalance } = useAuth();
  const fileRef = useRef(null);
  const [prev, setPrev] = useState(null);
  const [f, setF] = useState({ wallet: "aeps", total: "", mpin: "" });
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(0);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post("/payout/bulk/preview", fd); setPrev(data); setF({ ...f, total: "", mpin: "" }); } catch (e) { toast.error(errMsg(e)); setPrev(null); } finally { setBusy(false); fileRef.current.value = ""; }
  };
  const valid = prev?.rows.filter((r) => !r.error) || [];
  const matched = prev && Number(f.total) === prev.total_amount;
  const submit = async () => {
    setBusy(true);
    try {
      const rows = valid.map(({ holder_name, account_no, ifsc, bank, mobile, amount, mode }) => ({ holder_name, account_no, ifsc, bank, mobile, amount, mode }));
      const { data } = await api.post("/payout/bulk", { rows, total_amount: Number(f.total), wallet: f.wallet, mpin: f.mpin });
      toast.success(`Batch ${data.batch_no} uploaded — ${data.count} transfers are being sent`);
      setPrev(null); setKey(key + 1); setTimeout(refreshBalance, 3000);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="bulk-payout">
      <Card title="Bulk Upload — Move to Bank">
        <div className="flex flex-wrap items-center gap-3">
          <Btn variant="outline" onClick={downloadTemplate} data-testid="bulk-template-download"><Download className="h-4 w-4" />Download Excel template</Btn>
          <Btn onClick={() => fileRef.current.click()} loading={busy && !prev} data-testid="bulk-upload-button"><Upload className="h-4 w-4" />Upload filled Excel</Btn>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="bulk-file-input" />
          <span className="text-xs text-slate-500">Columns: AccountNumber, BeneName, BankName, BeneIFSCCode, BeneMobile, Amount, TransactionType (IMPS/NEFT/RTGS) · max 200 rows · amount more than ₹1000</span>
        </div>
        {prev && (
          <div className="mt-5 space-y-4">
            <div className="flex flex-wrap gap-4 text-sm" data-testid="bulk-preview-summary">
              <span>Valid rows: <b className="text-emerald-700">{prev.valid_count}</b></span>
              <span>Rows with errors: <b className="text-red-600">{prev.error_count}</b> {prev.error_count > 0 && "(will be skipped)"}</span>
              <span>Total amount: <b className="font-mono">{inr(prev.total_amount)}</b></span>
              <span>Charges: <b className="font-mono">{inr(prev.total_charge)}</b></span>
              <span>Total debit: <b className="font-mono">{inr(prev.total_amount + prev.total_charge)}</b></span>
            </div>
            <DataTable testid="bulk-preview-table" rows={prev.rows} columns={PREVIEW_COLS} />
            {valid.length > 0 && (
              <div className="grid items-end gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-4">
                <Field label="Source Wallet"><Select options={[{ value: "aeps", label: `AEPS (${inr(wallet.aeps_balance)})` }, { value: "main", label: `Main (${inr(wallet.balance)})` }]} value={f.wallet} onChange={(e) => setF({ ...f, wallet: e.target.value })} data-testid="bulk-wallet-select" /></Field>
                <Field label="Re-enter total amount">
                  <input type="number" className={`np-input font-mono ${f.total && !matched ? "border-red-400" : ""}`} value={f.total} onChange={(e) => setF({ ...f, total: e.target.value })} data-testid="bulk-total-input" />
                  {f.total && <span className={`text-[11px] font-semibold ${matched ? "text-emerald-700" : "text-red-600"}`} data-testid="bulk-total-match">{matched ? "Amount matched" : "Amount does not match"}</span>}
                </Field>
                <MpinField value={f.mpin} onChange={(v) => setF({ ...f, mpin: v })} />
                <Btn onClick={submit} loading={busy} disabled={!matched || f.mpin.length < 4} data-testid="bulk-submit-button">Send {valid.length} transfers</Btn>
              </div>
            )}
          </div>
        )}
      </Card>
      <Batches refreshKey={key} />
    </div>
  );
}
